import { useEffect, useState, useContext, useCallback } from "react";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { MultiSelect } from "primereact/multiselect";
import { Button } from "primereact/button";
import { getCountries } from "../../api/countryApi";
import { AuthContext } from "../../Context/AuthContext";

export default function LanguageForm({ visible, onHide, onSubmit, initialData, existingLanguages = [] }) {
  const [form, setForm] = useState({ code: "", name: "", countryIds: [] });
  const [countries, setCountries] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const { auth } = useContext(AuthContext);
  const token = auth?.token;

  const loadCountries = useCallback(async () => {
    if (!token) return;
    try {
      const res = await getCountries(token);
      setCountries(res.data.data || []);
    } catch (err) {
      console.error("Failed to load countries", err);
    }
  }, [token]);

  useEffect(() => {
    loadCountries();
  }, [loadCountries]);

  useEffect(() => {
    if (!visible) return;
    if (initialData) {
      setForm({
        code: initialData.code,
        name: initialData.name,
        countryIds: initialData.Countries?.map(c => c.id) || [],
      });
    } else {
      setForm({ code: "", name: "", countryIds: [] });
    }
  }, [visible, initialData]);

  const toSentenceCase = str => {
    if (!str) return "";
    return str
      .toLowerCase()
      .replace(/(^\s*\w|[\.\!\?]\s*\w)/g, c => c.toUpperCase());
  };

  const submitForm = async () => {
    const code = form.code.trim().toLowerCase();
    const name = toSentenceCase(form.name.trim());

    if (!code || !name) {
      alert("Language code and name are required");
      return;
    }

    const duplicateCode = existingLanguages.some(l =>
      l.code.toLowerCase() === code && l.id !== initialData?.id
    );

    const duplicateName = existingLanguages.some(l =>
      l.name.toLowerCase() === name.toLowerCase() && l.id !== initialData?.id
    );

    if (duplicateCode) {
      alert("Duplicate language code detected");
      return;
    }

    if (duplicateName) {
      alert("Duplicate language name detected");
      return;
    }

    setIsLoading(true);
    try {
      await onSubmit({
        code,
        name,
        countryIds: form.countryIds || [],
      });

      if (!initialData) setForm({ code: "", name: "", countryIds: [] });
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      const tag = e.target.tagName.toLowerCase();
      if (e.key === "Enter" && (tag === "input" || tag === "textarea")) {
        e.preventDefault();
        submitForm();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [form, initialData, existingLanguages]);

  return (
    <Dialog
      header={initialData ? "Edit Language" : "Add Language"}
      visible={visible}
      onHide={onHide}
      modal
      className="w-6"
    >
      <div className="flex flex-column gap-3 p-3">
        <div>
          <label>Language Name</label>
          <InputText
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="w-full"
          />
        </div>
        
        <div>
          <label>Language Code</label>
          <InputText
            value={form.code}
            onChange={(e) => setForm({ ...form, code: e.target.value })}
            className="w-full"
          />
        </div>

        <div>
          <label>Countries</label>
          <MultiSelect
            value={form.countryIds}
            options={countries}
            optionLabel="name"
            optionValue="id"
            onChange={(e) => setForm({ ...form, countryIds: e.value })}
            placeholder="Select Countries"
            className="w-full"
          />
        </div>

        <Button
          label={isLoading ? "Submitting..." : "Submit"}
          icon={isLoading ? "pi pi-spin pi-spinner" : ""}
          onClick={submitForm}
          disabled={isLoading}
          className=" p-button-block custom-splitbutton"
          style={{ backgroundColor: "#FC5F1CFF", color: "#fff" }}
        />
      </div>
    </Dialog>
  );
}
